import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Tween, ScrollTrigger } from "react-gsap";
import Background from "../Global/Background";
import Link from "../Global/Link";
import Video from "../Global/Video";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import { globalActions } from "../../context/global";
import Modal from "../Templates/Modal";
import Image from "../Global/Image";
import ImageLink from "../Media/ImageLink";

const SectionHeroVideo = (props) => {
  const { data, settings } = props;

  const desktopSectionRef = useRef();
  const mobileSectionRef = useRef();
  const [isDesktopHeroTallerThanScreen, setDesktopIsHeroTallerThanScreen] =
    useState(false);
  const [isMobileHeroTallerThanScreen, setMobileIsHeroTallerThanScreen] =
    useState(false);

  const [windowInnerHeight, setWindowInnerHeight] = useState(
    typeof window !== "undefined" && window?.innerHeight
  );
  const [windowInnerWidth, setWindowInnerWidth] = useState(
    typeof window !== "undefined" && window?.innerWidth
  );

  useEffect(() => {
    const checkTrailerPosition = () => {
      setDesktopIsHeroTallerThanScreen(
        desktopSectionRef?.current.clientHeight > window?.innerHeight
          ? true
          : false
      );

      // Dont update mobile var if height has changed
      if (
        typeof window !== "undefined" &&
        window.innerHeight !== windowInnerHeight &&
        window.innerWidth === windowInnerWidth
      ) {
        setWindowInnerHeight(window?.innerHeight);
        return;
      }
      setWindowInnerWidth(window?.innerWidth);
      setMobileIsHeroTallerThanScreen(
        mobileSectionRef?.current.clientHeight > window?.innerHeight
          ? true
          : false
      );
    };

    // Initial check
    checkTrailerPosition();

    // Check again on resize
    if (typeof window !== "undefined") {
      window.addEventListener("resize", checkTrailerPosition);
    }

    // Cleanup func
    return () => {
      window.removeEventListener("resize", checkTrailerPosition);
    };
  }, []);

  const platformsRef = useRef();
  const platformsMobileRef = useRef();

  const dispatch = useDispatch();
  const storeIsMobile = useSelector((state) => state.global.isMobile);

  const {
    logo,
    hero,
    hero_mobile,
    video_desktop,
    video_mobile,
    simple,
    fixed,
  } = data;

  const { trailer_link, trailer_label, preorder_link, preorder_label } =
    settings;

  const [isFirstVideoReady, setIsFirstVideoReady] = useState(false);
  const [isFirstPlaying, setIsFirstPlaying] = useState(false);
  const [pageReady, setPageReady] = useState(false);

  // https://vimeo.com/707734205 TEST DEV VIDEO
  const firstVideoRef = useRef();
  const buttonContainerRef = useRef();
  const modalRefMedia = useRef();

  const handleTrailerOpenClick = () => {
    modalRefMedia.current.openModal();
  };

  useEffect(() => {}, []);

  useLayoutEffect(() => {
    dispatch(globalActions.setIsMobile());
  }, []);

  useEffect(() => {
    const handleActivityFalse = () => {
      setIsFirstPlaying(false);
    };

    const handleActivityTrue = () => {
      setIsFirstPlaying(true);
    };

    // Recheck if mobile on window resize
    if (typeof window !== "undefined") {
      window.addEventListener("focus", handleActivityTrue);
      window.addEventListener("blur", handleActivityFalse);
    }

    setPageReady(true);

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", handleActivityTrue);
        window.removeEventListener("blur", handleActivityFalse);
      }
    };
  }, []);

  const firstVideoReadyHandler = () => {
    setIsFirstPlaying(true);
    setIsFirstVideoReady(true);
  };

  const preorderVisibilityToggle = () => {
    platformsRef?.current?.classList?.toggle("show-platforms");
  };
  const preorderMobileVisibilityToggle = () => {
    platformsMobileRef?.current?.classList?.toggle("show-platforms");
  };
  const preorderVisibilityShow = () => {
    platformsRef?.current?.classList?.add("show-platforms");
  };
  const preorderVisibilityHide = () => {
    platformsRef?.current?.classList?.remove("show-platforms");
  };

  return (
    <React.Fragment>
      <section
        ref={desktopSectionRef}
        className={`d-none d-md-flex brand-section brand-section-hero flex-column justify-content-center align-items-center brand-text-black w-100`}
      >
        <Background
          className={`w-100 position-relative brand-background-grey`}
          sm="0x750"
          md="0x750"
          lg="0x1080"
          src={hero?.filename}
        >
          {video_desktop && !storeIsMobile && (
            <Video
              containerClassName={`brand-video-hero brand-video-background ratio ratio-16x9 ${
                isFirstVideoReady
                  ? "brand-video-background-show"
                  : "brand-video-background-hide"
              }`}
              onVideoPlay={firstVideoReadyHandler}
              autoplay={true}
              background={true}
              light={false}
              loop={true}
              muted={true}
              video={video_desktop}
            />
          )}
          <div
            className={`position-absolute w-100 h-100 top-0 right-0 bottom-0 left-0 d-flex flex-column align-items-center justify-content-center pb-5 brand-logo-container ${
              pageReady ? "brand-logo-container-fade" : ""
            }`}
          >
            <div className="d-flex flex-column justify-content-center align-items-center">
              <Link to="/">
                <Image
                  src={settings?.logo?.filename}
                  alt={settings?.logo?.alt}
                  sm="600x0"
                  md="600x0"
                  lg="800x0"
                  className="w-100 pb-3"
                />
              </Link>

              <div
                onClick={preorderVisibilityToggle}
                onMouseLeave={preorderVisibilityHide}
                className="position-relative brand-preorder-hero mb-3"
              >
                {preorder_label && (
                  <h3
                    onMouseEnter={preorderVisibilityShow}
                    className="brand-btn-text brand-font brand-text-blue d-flex align-items-center text-decoration-underline brand-cursor"
                  >
                    {preorder_label}
                  </h3>
                )}

                <div
                  ref={platformsRef}
                  className="rounded position-absolute justify-content-between align-items-center brand-preorder-reveal brand-text-white"
                  style={{
                    width: "50vw",
                    transform: "translateX(-50%)",
                    left: "50%",
                  }}
                >
                  {settings?.platforms?.length &&
                    settings.platforms.map((platform, index) => {
                      return (
                        <ImageLink
                          key={index}
                          data={platform}
                          sm="0x40"
                          md="0x40"
                          lg="0x40"
                          imageClassName="p-3 brand-preorder-dropdown-item platform-icon-scaler"
                        />
                      );
                    })}
                </div>
              </div>
              {trailer_link?.url && pageReady && (
                <div
                  className={`pb-3 ${
                    isDesktopHeroTallerThanScreen ? "d-flex" : "d-none"
                  } flex-column align-items-between text-center brand-text-white`}
                >
                  {trailer_link && trailer_label && (
                    <button
                      className="mb-4 brand-link btn brand-btn brand-btn-black"
                      onClick={handleTrailerOpenClick}
                    >
                      <span className="brand-btn-text brand-font">
                        {trailer_label}
                      </span>
                    </button>
                  )}
                  <Tween
                    from={{
                      y: "-20px",
                    }}
                    to={{
                      y: "0",
                    }}
                    duration={1}
                    repeat={-1}
                    ease="Bounce.easeOut"
                    yoyo={true}
                    delay={2}
                  >
                    <span className="fs-2 opacity-50">
                      <FontAwesomeIcon icon="chevron-down" />
                    </span>
                  </Tween>
                </div>
              )}
            </div>

            {trailer_link?.url && pageReady && (
              <div
                className={`position-absolute pb-3 ${
                  isDesktopHeroTallerThanScreen ? "d-none" : "d-flex bottom-0"
                } flex-column align-items-between text-center brand-text-white`}
              >
                {trailer_link && trailer_label && (
                  <button
                    className="mb-4 brand-link btn brand-btn brand-btn-black"
                    onClick={handleTrailerOpenClick}
                  >
                    <span className="brand-btn-text brand-font">
                      {trailer_label}
                    </span>
                  </button>
                )}
                <Tween
                  from={{
                    y: "-20px",
                  }}
                  to={{
                    y: "0",
                  }}
                  duration={1}
                  repeat={-1}
                  ease="Bounce.easeOut"
                  yoyo={true}
                  delay={2}
                >
                  <span className="fs-2 opacity-50">
                    <FontAwesomeIcon icon="chevron-down" />
                  </span>
                </Tween>
              </div>
            )}
          </div>
        </Background>
      </section>

      <section
        className={`d-none d-md-flex ratio ratio-16x9 flex-column justify-content-center align-items-center brand-background-black w-100 brand-background-green`}
      >
        {/* <p>
          TODO PUT DESKTOP BACKGROUND IN HERE
        </p> */}
      </section>

      <section
        ref={mobileSectionRef}
        className={`d-flex d-md-none brand-section brand-section-hero flex-column justify-content-center align-items-center brand-text-black w-100`}
      >
        <Background
          className={`w-100 position-relative brand-background-grey`}
          sm="0x750"
          md="0x750"
          lg="0x1080"
          src={hero_mobile?.filename}
        >
          {video_mobile && (
            <Video
              containerClassName={`brand-video-hero brand-video-background ratio ratio-9x16 ${
                isFirstVideoReady
                  ? "brand-video-background-show"
                  : "brand-video-background-hide"
              }`}
              onVideoPlay={firstVideoReadyHandler}
              autoplay={true}
              background={true}
              light={false}
              loop={true}
              muted={true}
              video={video_mobile}
            />
          )}
        </Background>
        {logo && (
          <div
            className={`position-absolute w-100 h-100 top-0 right-0 bottom-0 left-0 border-5 d-flex flex-column align-items-center justify-content-top p-3 brand-logo-container ${
              pageReady ? "brand-logo-container-fade" : ""
            }`}
          >
            <Link to="/">
              <Image
                src={settings?.logo?.filename}
                alt={settings?.logo?.alt}
                sm="600x0"
                md="600x0"
                lg="800x0"
                className="w-100 pb-5"
              />
            </Link>

            <div
              className="position-relative brand-preorder-hero"
              style={{ marginTop: "5rem" }}
            >
              {preorder_label && (
                <h3
                  onClick={preorderMobileVisibilityToggle}
                  className="brand-btn-text brand-font brand-text-blue d-flex justify-content-center align-items-center text-decoration-underline brand-cursor"
                >
                  {preorder_label}
                </h3>
              )}

              <div
                ref={platformsMobileRef}
                aria-labelledby="platformsToggle"
                className="position-absolute d-flex justify-content-between align-items-center brand-preorder-reveal brand-background-black brand-text-white"
                style={{
                  width: "50vw",
                  transform: "translateX(-50%)",
                  left: "50%",
                }}
              >
                <div className="row d-flex justify-content-between align-items-center">
                  {settings?.platforms?.length &&
                    settings.platforms.map((platform, index) => {
                      return (
                        <ImageLink
                          key={index}
                          data={platform}
                          sm="0x40"
                          md="0x40"
                          lg="0x40"
                          imageClassName="p-3 brand-preorder-dropdown-item platform-icon-scaler"
                        />
                      );
                    })}
                </div>
              </div>

              {trailer_link?.url && pageReady && (
                <div
                  className={`py-3 ${
                    isMobileHeroTallerThanScreen ? "d-flex" : "d-none"
                  } flex-column align-items-between text-center brand-text-white`}
                >
                  {trailer_link && trailer_label && (
                    <button
                      className="mb-4 brand-link btn brand-btn brand-btn-black"
                      onClick={handleTrailerOpenClick}
                    >
                      <span className="brand-btn-text brand-font">
                        {trailer_label}
                      </span>
                    </button>
                  )}
                  <Tween
                    from={{
                      y: "-20px",
                    }}
                    to={{
                      y: "0",
                    }}
                    duration={1}
                    repeat={-1}
                    ease="Bounce.easeOut"
                    yoyo={true}
                    delay={2}
                  >
                    <span className="fs-2 opacity-50">
                      <FontAwesomeIcon icon="chevron-down" />
                    </span>
                  </Tween>
                </div>
              )}
            </div>

            {trailer_link?.url && pageReady && (
              <div
                className={`position-absolute pb-3 ${
                  isMobileHeroTallerThanScreen ? "d-none" : "d-flex bottom-0"
                } flex-column align-items-between text-center brand-text-white`}
              >
                {trailer_link && trailer_label && (
                  <button
                    className="mb-4 brand-link btn brand-btn brand-btn-black"
                    onClick={handleTrailerOpenClick}
                  >
                    <span className="brand-btn-text brand-font">
                      {trailer_label}
                    </span>
                  </button>
                )}
                <Tween
                  from={{
                    y: "-20px",
                  }}
                  to={{
                    y: "0",
                  }}
                  duration={1}
                  repeat={-1}
                  ease="Bounce.easeOut"
                  yoyo={true}
                  delay={2}
                >
                  <span className="fs-2 opacity-50">
                    <FontAwesomeIcon icon="chevron-down" />
                  </span>
                </Tween>
              </div>
            )}
          </div>
        )}
      </section>
      <section
        className={`d-flex d-md-none ratio ratio-9x16 flex-column justify-content-center align-items-center brand-background-black w-100 brand-background-green`}
      >
        {/* <p>
          TODO PUT MOBILE BACKGROUND IN HERE
        </p> */}
      </section>

      <Modal
        ref={modalRefMedia}
        modalClassName="d-flex justify-content-center align-items-center"
        modalDialogClassName="brand-trailer-dialog-modal"
      >
        <Video
          containerClassName="h-100 w-100"
          videoYoutubeUrl={trailer_link?.url}
        />
      </Modal>
    </React.Fragment>
  );
};

export default SectionHeroVideo;

SectionHeroVideo.propTypes = {
  doc: PropTypes.object,
};

SectionHeroVideo.defaultProps = {
  doc: null,
};
